import { createReducer, on } from '@ngrx/store';

import { PICKUP_STATUS, PickUpStatus } from 'src/app/shared/services/frodo';
import { IlluminationMode } from 'src/app/shared/types/illumination';

import { Schedule, Space } from './interfaces';
import * as RemotePickupActions from './remote-pickup.actions';

export interface PickupIndicatorState {
  image?: string;
  color?: string;
  mode?: IlluminationMode;
}

export interface RemotePickupState {
  code?: string;
  indicator?: PickupIndicatorState | null;
  state: {
    status: PickUpStatus;
    position?: number;
    queuedAt?: number;
    startedAt?: number;
    confirmAt?: number;
  } | null;
  schedule?: Schedule;
  spaces?: Array<Space>;
  isOverflowArea?: boolean;
  shouldHandleDriverNotification?: boolean;
  isOffline?: boolean;
}

export const initialState: RemotePickupState = {
  code: null,
  indicator: null,
  schedule: null,
  spaces: null,
  state: null,
  isOverflowArea: false,
  shouldHandleDriverNotification: false,
  isOffline: false,
};

export const reducer = createReducer(
  initialState,

  on(RemotePickupActions.addPickup, (state, { code }) => ({
    ...state,
    code,
  })),

  on(RemotePickupActions.addPickupSuccess, (state, { queuedAt, position, status, ...payload }) => ({
    ...state,
    ...payload,
    state: {
      ...state.state,
      queuedAt,
      position,
      status,
    },
  })),

  on(RemotePickupActions.addPickupFailure, (state) => ({
    ...state,
    code: null,
    order: null,
    indicator: null,
    schedule: null,
    spaces: null,
    state: {
      position: null,
      queuedAt: null,
      startedAt: null,
      confirmAt: null,
      status: null,
    },
  })),

  on(RemotePickupActions.setPickupOrder, (state, { code, spaces, schedule }) => ({
    ...state,
    code,
    spaces,
    schedule,
  })),

  on(RemotePickupActions.updatePickupStateSuccess, (state, { status, position }) => {
    const queuedAt = state.state?.queuedAt;
    const startedAt = state.state?.startedAt;
    const confirmPickupEnteredAt = state.state?.confirmAt;
    return {
      ...state,
      state: {
        status,
        ...(position !== undefined ? { position } : {}),
        ...(queuedAt ? { queuedAt } : {}),
        ...(startedAt ? { startedAt } : {}),
        ...(confirmPickupEnteredAt ? { confirmAt: confirmPickupEnteredAt } : {}),
      },
    };
  }),

  on(RemotePickupActions.setPickupActive, (state) => {
    const startedAt = state.state?.startedAt;
    const confirmPickupEnteredAt = state.state?.confirmAt;
    return {
      ...state,
      state: {
        status: PICKUP_STATUS.ACTIVE,
        ...(startedAt ? { startedAt } : {}),
        ...(confirmPickupEnteredAt ? { confirmAt: confirmPickupEnteredAt } : {}),
      },
    };
  }),

  on(RemotePickupActions.clearRemotePickup, () => ({
    ...initialState,
  })),

  on(RemotePickupActions.startPickupSuccess, (state, action) => ({
    ...state,
    schedule: action.scheduleId
      ? {
          id: action.scheduleId,
        }
      : state.schedule,
    spaces: action.spaces ?? state.spaces ?? [],
    indicator: action.indicator ?? null,
    state: {
      ...state.state,
      status: PICKUP_STATUS.ACTIVE,
      startedAt: action.startedAt ?? null, //TODO: check if this is correct
    },
  })),

  on(RemotePickupActions.startPickupFailure, (state) => ({
    ...state,
    code: null,
    schedule: null,
    spaces: null,
    indicator: null,
    state: null,
  })),

  on(
    RemotePickupActions.setShouldHandleDriverNotification,
    (state, { shouldHandleDriverNotification }) => ({
      ...state,
      shouldHandleDriverNotification,
    })
  ),

  on(RemotePickupActions.setConfirmAt, (state, { confirmAt: confirmPickupEnteredAt }) => {
    return {
      ...state,
      state: {
        ...state.state,
        confirmAt: confirmPickupEnteredAt,
      },
    };
  })
);
